import { Fragment, useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar
} from '@carbon/react';

import TableFiltersToolbar from 'features/all-projects/components/TableFiltersToolbar';
import { SORT } from 'features/all-projects/filters';
import SiteProjectsTable from 'features/all-projects/SiteProjectsTable';
import useSitesQuery from 'shared/api/projects/useSitesQuery';
import Pagination, { usePagination } from 'shared/components/Pagination';
import { formatDate } from 'utils/dates';
import useDebouncedState from 'utils/hooks/useDebouncedState';
import styles from 'theme/features/all-projects/all-projects.module.scss';

const SitesTable = ({ isArchived = false, isConfidential, isFinal }) => {
  const pagination = usePagination();

  const [expandedSiteId, setExpandedSiteId] = useState(null);
  const [currentSort, setCurrentSort] = useState(SORT.startDate);
  const {
    value: searchValue,
    debouncedValue: currentSearchValue,
    setValue: setSearchValue,
    reset: resetSearchValue
  } = useDebouncedState({
    initialValue: '',
    onDebounce: pagination.resetPage
  });

  const { data: sites, refetch: refetchSites } = useSitesQuery({
    sort: currentSort,
    page: pagination.page,
    pageSize: pagination.pageSize,
    archived: isArchived,
    confidented: isConfidential,
    finaled: isFinal,
    ...(currentSearchValue && { search: currentSearchValue })
  });

  const handleReset = () => {
    setCurrentSort(SORT.startDate);
    pagination.reset();
    setExpandedSiteId(null);
    resetSearchValue();
  };

  const tableData =
    sites?.map((x) => ({
      id: x.id.toString(),
      address: x.address ?? 'Unknown',
      location: `${x.city}, ${x.country}`,
      createdOn: formatDate(x.createdOn),
      updatedOn: x.updatedOn ? formatDate(x.updatedOn) : '-',
      notes: x.notes
    })) ?? [];

  const headers = [
    { key: 'address', header: 'Address' },
    { key: 'location', header: 'Location' },
    { key: 'createdOn', header: 'Started On' },
    { key: 'updatedOn', header: 'Last Modified' },
    { key: 'notes', header: 'Notes' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={tableData}
      size="sm"
      render={({ rows, getTableContainerProps, getToolbarProps, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer
          title="Known Sites"
          description="All the existing IBM sites you are working with, along with their associated projects. Expand each site to view the full list of project"
          className={styles.table}
          {...getTableContainerProps()}
        >
          <TableToolbar {...getToolbarProps()}>
            <TableFiltersToolbar
              onReset={handleReset}
              onSortChange={setCurrentSort}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearchReset={() => {
                resetSearchValue();
                pagination.resetPage();
              }}
            />
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableExpandHeader id="expand" />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const site = sites?.[index];
                const isExpanded = expandedSiteId === row.id;

                return (
                  <Fragment key={row.id}>
                    <TableExpandRow
                      expandHeader="expand"
                      {...getRowProps({ row })}
                      isExpanded={isExpanded}
                      onExpand={() => setExpandedSiteId(isExpanded ? null : row.id)}
                    >
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableExpandRow>
                    <TableExpandedRow colSpan={headers.length + 1} className={styles['expand-row']}>
                      {site && isExpanded && (
                        <SiteProjectsTable
                          site={site}
                          isArchived={isArchived}
                          isConfidential={isConfidential}
                          isFinal={isFinal}
                          onAllProjectsRemoved={refetchSites}
                          searchValue={currentSearchValue}
                        />
                      )}
                    </TableExpandedRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Pagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            isLastPage={!sites || sites.length < pagination.pageSize}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </TableContainer>
      )}
    />
  );
};

export default SitesTable;
